// WorkflowStepper (Spec §7 / P3) — a visible multi-step path: each step is
// done / current / locked (with a lock reason). Horizontal on desktop, vertical
// on mobile. Steps with an onSelect are clickable; locked steps show their
// reason and never navigate. Used by the project pipeline, New Disclosure, the
// disclosure ladder, org onboarding, the BYOK cut-over, and keypair setup — one
// component so the pattern is learned once and applied everywhere.

import type { ReactNode } from 'react'
import { Check, Lock } from 'lucide-react'

export type StepStatus = 'completed' | 'active' | 'pending' | 'locked'

export interface WorkflowStep {
  key: string
  label: string
  /** short caption under the label (optional) */
  hint?: ReactNode
  status: StepStatus
  /** shown as a tooltip/inline note when the step is locked */
  lockReason?: string
  /** when provided and the step is not locked, the step is clickable */
  onSelect?: () => void
}

interface WorkflowStepperProps {
  steps: WorkflowStep[]
  /** 'horizontal' (desktop default) collapses to vertical under sm */
  orientation?: 'horizontal' | 'vertical'
  className?: string
  'aria-label'?: string
}

function dotClasses(status: StepStatus): string {
  switch (status) {
    case 'completed':
      return 'bg-emerald-500 text-white border-emerald-500'
    case 'active':
      return 'bg-iflow-600 text-white border-iflow-600 ring-4 ring-iflow-100'
    case 'locked':
      return 'bg-slate-100 text-slate-400 border-slate-200'
    default:
      return 'bg-white text-slate-400 border-slate-300'
  }
}

function labelClasses(status: StepStatus): string {
  switch (status) {
    case 'completed':
      return 'text-emerald-700'
    case 'active':
      return 'text-iflow-900 font-semibold'
    case 'locked':
      return 'text-slate-400'
    default:
      return 'text-slate-500'
  }
}

export default function WorkflowStepper({
  steps,
  orientation = 'horizontal',
  className = '',
  'aria-label': ariaLabel = 'Workflow progress',
}: WorkflowStepperProps) {
  const vertical = orientation === 'vertical'
  return (
    <ol
      aria-label={ariaLabel}
      className={
        (vertical
          ? 'flex flex-col gap-0'
          : 'flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-0') + ' ' + className
      }
    >
      {steps.map((step, i) => {
        const clickable = !!step.onSelect && step.status !== 'locked'
        const Tag = clickable ? 'button' : 'div'
        const isLast = i === steps.length - 1
        return (
          <li
            key={step.key}
            aria-current={step.status === 'active' ? 'step' : undefined}
            className={
              vertical
                ? 'relative flex items-start gap-3 pb-5 last:pb-0'
                : 'relative flex items-start sm:flex-1 sm:flex-col sm:items-center'
            }
          >
            {/* connector line */}
            {!isLast && (
              <span
                aria-hidden
                className={
                  vertical
                    ? `absolute left-[15px] top-8 h-[calc(100%-1.75rem)] w-0.5 ${
                        step.status === 'completed' ? 'bg-emerald-300' : 'bg-slate-200'
                      }`
                    : `absolute left-1/2 top-4 hidden h-0.5 w-full sm:block ${
                        step.status === 'completed' ? 'bg-emerald-300' : 'bg-slate-200'
                      }`
                }
              />
            )}
            <Tag
              type={clickable ? 'button' : undefined}
              onClick={clickable ? step.onSelect : undefined}
              disabled={Tag === 'button' ? !clickable : undefined}
              title={step.status === 'locked' ? step.lockReason : undefined}
              className={
                'group z-10 flex items-center gap-3 text-left ' +
                (vertical ? '' : 'sm:flex-col sm:items-center sm:text-center ') +
                (clickable ? 'cursor-pointer' : 'cursor-default')
              }
            >
              <span
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-sm font-semibold transition-colors ${dotClasses(
                  step.status,
                )} ${clickable ? 'group-hover:brightness-95' : ''}`}
              >
                {step.status === 'completed' ? (
                  <Check size={16} aria-hidden />
                ) : step.status === 'locked' ? (
                  <Lock size={14} aria-hidden />
                ) : (
                  i + 1
                )}
              </span>
              <span className={vertical ? '' : 'sm:mt-1.5'}>
                <span className={`block text-sm ${labelClasses(step.status)}`}>{step.label}</span>
                {step.status === 'locked' && step.lockReason ? (
                  <span className="block text-xs text-slate-400">{step.lockReason}</span>
                ) : step.hint ? (
                  <span className="block text-xs text-slate-400">{step.hint}</span>
                ) : null}
              </span>
            </Tag>
          </li>
        )
      })}
    </ol>
  )
}
